/**
 * The Journal's data layer. Same shape as `documents-api.ts`: keys nest under
 * the project, loading and error handling stay at the call site.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CrewCountOnDate,
  JournalDaySummary,
  JournalEntry,
  JournalEntryInput,
  JournalEntryPatch,
  JournalNote,
  JournalNoteCreated,
  JournalNoteInput,
  NoteExtractionSuggestion,
} from "@gb/contracts";
import { api, errorMessage } from "@/api/client";

/** `?from=&to=&q=` on `listNotes`. Every part optional, as the contract has it. */
export type JournalNoteRange = { from?: string; to?: string; q?: string };

/** `?from=&to=` on `days`. Both required — the screen always knows its window. */
export type JournalDayRange = { from: string; to: string };

export const journalKeys = {
  list: (projectId: string) => ["project", projectId, "journal"] as const,
  detail: (projectId: string, entryId: string) =>
    ["project", projectId, "journal", entryId] as const,
  // Siblings of the keys above rather than a family of their own, so
  // invalidating `list` takes the notes and the day summaries with it and
  // nothing else — `architecture/0002` §"Query keys were reshaped". Filing a
  // note can write a `journal_entries` row, so the two really are one cache.
  notes: (projectId: string, range: JournalNoteRange) =>
    ["project", projectId, "journal", "notes", range] as const,
  days: (projectId: string, range: JournalDayRange) =>
    ["project", projectId, "journal", "days", range] as const,
};

/**
 * `GET /projects/:id/crew/count`, keyed under `crew` rather than `journal`.
 *
 * The route belongs to the crew domain and the only thing that reads it is the
 * journal composer, so the key lives here beside its consumer while sharing
 * `crewKeys.roster`'s prefix — onboarding somebody changes both answers, and a
 * key under `journal` would leave this one stale after it.
 */
export const crewCountKeys = {
  onDate: (projectId: string, date: string) =>
    ["project", projectId, "crew", "count", date] as const,
};

export function useJournal(projectId: string | undefined) {
  return useQuery({
    queryKey: journalKeys.list(projectId ?? ""),
    enabled: Boolean(projectId),
    queryFn: async (): Promise<JournalEntry[]> => {
      const res = await api.journals.list({ params: { id: projectId! } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.entries;
    },
  });
}

export function useJournalEntry(projectId: string | undefined, entryId: string | undefined) {
  return useQuery({
    queryKey: journalKeys.detail(projectId ?? "", entryId ?? ""),
    enabled: Boolean(projectId && entryId),
    queryFn: async (): Promise<JournalEntry> => {
      const res = await api.journals.get({
        params: { id: projectId!, entryId: entryId! },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.entry;
    },
  });
}

export function useCreateJournalEntry(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: JournalEntryInput): Promise<JournalEntry> => {
      const res = await api.journals.create({ params: { id: projectId }, body: input });
      // 409 is the one-per-day rule, and its message names the date — passing it
      // through unchanged is more useful than a generic failure.
      if (res.status !== 201) throw new Error(errorMessage(res));
      return res.body.entry;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: journalKeys.list(projectId) });
    },
  });
}

export function usePatchJournalEntry(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      entryId: string;
      patch: JournalEntryPatch;
    }): Promise<JournalEntry> => {
      const res = await api.journals.patch({
        params: { id: projectId, entryId: vars.entryId },
        body: vars.patch,
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.entry;
    },
    onSuccess: (entry) => {
      queryClient.setQueryData(journalKeys.detail(projectId, entry.id), entry);
      void queryClient.invalidateQueries({ queryKey: journalKeys.list(projectId) });
    },
  });
}

export function useApproveJournalEntry(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { entryId: string; sentTo?: string[] }): Promise<JournalEntry> => {
      const res = await api.journals.approve({
        params: { id: projectId, entryId: vars.entryId },
        body: vars.sentTo ? { sentTo: vars.sentTo } : {},
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.entry;
    },
    onSuccess: (entry) => {
      queryClient.setQueryData(journalKeys.detail(projectId, entry.id), entry);
      void queryClient.invalidateQueries({ queryKey: journalKeys.list(projectId) });
    },
  });
}

export function useDeleteJournalEntry(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (entryId: string): Promise<void> => {
      const res = await api.journals.remove({
        params: { id: projectId, entryId },
      });
      if (res.status !== 204) throw new Error(errorMessage(res));
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: journalKeys.list(projectId) });
    },
  });
}

// ------------------------------------------------------------------
// Notes, day summaries, and the composer's headcount suggestion
// ------------------------------------------------------------------
//
// A **note** is anything anybody files against a date; a **daily record** is
// the day's signed-off account, and it exists only because somebody flagged a
// note as being it. See `project_intelligence/product/prd-journal.md` §6.

/**
 * The notes in a date range, optionally filtered by a substring of the body.
 *
 * Omitting both dates returns everything, matching `list`. `q` is browse and
 * filter, not retrieval — the field says "Search", not "Ask".
 */
export function useJournalNotes(projectId: string, range: JournalNoteRange) {
  return useQuery({
    queryKey: journalKeys.notes(projectId, range),
    enabled: Boolean(projectId),
    queryFn: async (): Promise<JournalNote[]> => {
      const res = await api.journals.listNotes({
        params: { id: projectId },
        // Built key by key rather than spread, so an absent bound is an absent
        // query parameter and never the literal string "undefined".
        query: {
          ...(range.from ? { from: range.from } : {}),
          ...(range.to ? { to: range.to } : {}),
          ...(range.q?.trim() ? { q: range.q.trim() } : {}),
        },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.notes;
    },
  });
}

/**
 * The one query the whole screen runs on: what each date in the window holds.
 *
 * **Only dates that hold something come back.** The calendar's *Not logged*
 * state and the catch-up queue are both derived from a date being *absent*
 * here — there is no row anywhere for a day nobody recorded, and writing one
 * is what `data-model/0004` §1 rejected.
 *
 * `windowStart` is the project's start date, or null when it was never set.
 */
export function useJournalDays(projectId: string, range: JournalDayRange) {
  return useQuery({
    queryKey: journalKeys.days(projectId, range),
    enabled: Boolean(projectId && range.from && range.to),
    queryFn: async (): Promise<{
      days: JournalDaySummary[];
      windowStart: string | null;
    }> => {
      const res = await api.journals.days({
        params: { id: projectId },
        query: { from: range.from, to: range.to },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return { days: res.body.days, windowStart: res.body.windowStart };
    },
  });
}

/**
 * File a note, and optionally make it the day's record.
 *
 * The whole journal cache is invalidated on success rather than any one key:
 * a flagged note writes the date's `journal_entries` row, changes that date's
 * summary, and can clear another note's flag and the day's sign-off with it.
 * `journalKeys.list` is the prefix all three sit under.
 */
export function useCreateJournalNote(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<JournalNoteCreated, Error, JournalNoteInput>({
    mutationFn: async (input: JournalNoteInput): Promise<JournalNoteCreated> => {
      const res = await api.journals.createNote({
        params: { id: projectId },
        body: input,
      });
      // 200 means this note was already filed — the composer sent an id it had
      // sent before, which is a save retried after a reply went missing. The
      // body is the same one the first attempt would have returned, so it is a
      // success, and the only honest one: the note exists.
      //
      // 409 is the narrow race where two people flag the same date in the same
      // instant; its message says so, and passing it through beats a generic
      // failure the user cannot act on.
      if (res.status !== 201 && res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: journalKeys.list(projectId) });
    },
  });
}

/**
 * Ask what the draft describes, while it is still a draft.
 *
 * A mutation rather than a query, because it is not a cacheable read of
 * server state: the same text asked twice is the same question, but the thing
 * being asked about lives in a textarea, and a query keyed on prose would fill
 * the cache with one entry per keystroke.
 *
 * The suggestion is never written anywhere by this call. It populates the
 * composer's Deliveries and Incidents sections, where somebody corrects or
 * deletes it before the note is filed — `document-ai/0002`.
 */
export function useExtractFromDraft(projectId: string) {
  return useMutation<NoteExtractionSuggestion, Error, string>({
    mutationFn: async (body: string): Promise<NoteExtractionSuggestion> => {
      const res = await api.journals.extractFromDraft({
        params: { id: projectId },
        body: { body },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
}

/**
 * How many people held an open period at this project on one date.
 *
 * **Advisory, and never attendance** — a period is a membership, so somebody
 * can hold open periods at two sites at once (`data-model/0003`). It exists to
 * pre-fill the composer's headcount stepper, where the user accepts it, types
 * over it, or clears it back to not recorded.
 *
 * `date` is nullable so the composer can hold the hook while it is closed and
 * has no date to ask about. The response echoes the date back, which is what
 * lets a late answer for a date the user has since changed be ignored.
 */
export function useCrewCountOn(projectId: string, date: string | null) {
  return useQuery({
    queryKey: crewCountKeys.onDate(projectId, date ?? ""),
    enabled: Boolean(projectId && date),
    queryFn: async (): Promise<CrewCountOnDate> => {
      const res = await api.crew.countOn({
        params: { id: projectId },
        query: { date: date! },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
}
